import { objectIsArray } from '../../../../utils/array';

import { findIndex, ListIterateeCustom } from 'lodash';
import * as React from 'react';

import {
  IconLicenseAllMedia,
  IconLicenseCaseStudy,
  IconLicenseIndustrial,
  IconLicensePodcast,
  IconLicenseRadio,
  IconLicenseShowReel,
  IconLicenseSocialMedia,
  IconLicenseTheatrical,
  IconLicenseTV,
  IconLicenseTVEditorial,
  IconLicenseTVWeb,
  IconLicenseWeb,
} from '../../../atoms/images/Icon';

import Button from '../../../atoms/buttons/Button';
import LicenseContextualParam from '../../../molecules/forms/license-builder/SelectContextualParam';
import LicenseLocation from '../../../molecules/forms/license-builder/SelectLocation';
import LicenseMedia from '../../../molecules/forms/license-builder/SelectMedia';
import LicenseSpotCount from '../../../molecules/forms/license-builder/SelectSpotCount';
import LicenseTerm from '../../../molecules/forms/license-builder/SelectTerm';
import LicenseTerritory from '../../../molecules/forms/license-builder/SelectTerritory';

interface IMediaTypeDefinitions {
  id: LicenseMediaTypeIdentifier;
  label?: string;
  icon: JSX.Element;
  hint?: string;
  contextualSubtitle?: string;
}

export interface IComponentHint {
  id: LicenseComponentIdentifiers;
  hint: string | React.ReactElement<any>;
}

function getStepNumber(predicate: ListIterateeCustom<ILicenseBuildingStepInfo, boolean>) {
  return 1 + findIndex(
    licenseBuildingSteps,
    typeof predicate === 'string' ? { id: predicate as LicenseComponentName } : predicate,
  )! as LicenseBuildingStepNumber;
}

export const contextualParamMapping: ILicenseContextualParamMapping[] = [
  {
    medium: [2001, 2002, 2003, 2006, 2007],
    param: 'broadcast_territories',
    type: 'BroadcastTerritory',
  },
  {
    medium: [2005],
    param: 'audience_sizes',
    type: 'AudienceSize',
  },
  {
    medium: [2014, 2015],
    param: 'production_budgets',
    type: 'ProductionBudget',
  },
  {
    medium: [2013],
    param: 'episode_budgets',
    type: 'EpisodeBudget',
  },
  {
    medium: [2004, 2008, 2009, 2010, 2011, 2012, 2016],
    param: 'producer_locations',
    type: 'ProducerLocation',
  },
];

export const programmingMedia = [2013, 2014, 2015];

export const licenseBuildingSteps: Array<ILicenseBuildingStepInfo | ILicenseBuildingStepInfo[]> = [
  {
    id: 'media',
    component: LicenseMedia,
  },
  {
    id: 'terms',
    component: LicenseTerm,
  },
  {
    id: 'spot_counts',
    component: LicenseSpotCount,
  },
  [
    {
      id: 'audience_sizes',
      component: LicenseContextualParam,
    },
    {
      id: 'production_budgets',
      component: LicenseContextualParam,
    },
    {
      id: 'episode_budgets',
      component: LicenseContextualParam,
    },
    {
      id: 'broadcast_territories',
      component: LicenseTerritory,
    },
    {
      id: 'producer_locations',
      component: LicenseLocation,
    },
  ],
];

export const mediumStep = getStepNumber('media');
export const termStep = getStepNumber('terms');
export const spotCountStep = getStepNumber('spot_counts');
export const contextualParamStep = getStepNumber((stepInfo) => objectIsArray(stepInfo));

export const excludedMediaType: LicenseMediaTypeIdentifier[] = [2001, 2003, 2008, 2009, 2010];

// tslint:disable:max-line-length
export const mediaTypeDefinitions: IMediaTypeDefinitions[] = [
  {
    id: 2001,
    label: 'TV + Web Streaming',
    icon: <IconLicenseTVWeb />,
    hint: 'For use on broadcast television, network, cable and satellite programming as well as the use on streaming websites and all forms of social media.',
  },
  {
    id: 2002,
    icon: <IconLicenseTV />,
    hint: 'For use on broadcast television, network, cable and satellite programming.',
  },
  {
    id: 2003,
    icon: <IconLicenseTVEditorial />,
  },
  {
    id: 2004,
    icon: <IconLicenseWeb />,
    hint: 'For use on streaming websites (Youtube, Vimeo etc.), all forms of social media, and OTT.',
    contextualSubtitle: 'Web Streaming licenses are valid for the world.',
  },
  {
    id: 2012,
    icon: <IconLicenseSocialMedia />,
    hint: 'For use on all forms of paid or organic social media (including Tik Tok), not including streaming websites.',
    contextualSubtitle: 'Social media licenses are valid for the world.',
  },
  {
    id: 2005,
    icon: <IconLicenseIndustrial />,
    hint: 'For use in non-broadcast, internal and external forms of advertising. Incudes in-store advertising, events, internal company use and out-of-home.',
    contextualSubtitle: 'Industrial & Closed Circuit licenses are valid for the world.',
  },
  {
    id: 2006,
    icon: <IconLicenseRadio />,
    hint: 'For use on broadcast AM/FM radio, as well as satellite radio.',
  },
  {
    id: 2007,
    icon: <IconLicenseTheatrical />,
    hint: 'For use in theatres as pre-film on-screen advertising.',
  },
  {
    id: 2008,
    icon: <IconLicenseShowReel />,
    hint: 'For use as background music on a single company\'s show-reel.',
    contextualSubtitle: 'Show Reel licenses are valid for the world.',
  },
  {
    id: 2016,
    icon: <IconLicenseShowReel />,
    hint: 'For use on case studies and as background music on a single company\'s show-reel.',
    contextualSubtitle: 'Case study and BTS licenses are valid for the world.',
  },
  {
    id: 2009,
    icon: <IconLicenseCaseStudy />,
    hint: 'For use on a case study film.',
    contextualSubtitle: 'Case Study licenses are valid for the world.',
  },
  {
    id: 2010,
    icon: <IconLicenseAllMedia />,
    hint: 'Includes all forms of advertising media.',
    contextualSubtitle: 'All Media licenses are valid for the world.',
  },
  {
    id: 2013,
    icon: <IconLicenseCaseStudy />,
    hint: 'For use on TV shows.',
    contextualSubtitle: 'All TV programming licenses are for perpetuity, budget ranges are in USD.',
  },
  {
    id: 2014,
    icon: <IconLicenseTheatrical />,
    hint: 'For use on film productions.',
    contextualSubtitle: 'All film production licenses are for perpetuity, budget ranges are in USD.',
  },
  {
    id: 2015,
    icon: <IconLicenseTheatrical />,
    hint: 'For use on film trailers.',
    contextualSubtitle: 'All film production licenses are for perpetuity, budget ranges are in USD.',
  },
  {
    id: 2011,
    icon: <IconLicensePodcast />,
    hint: 'For use in podcast advertising or as in-program music.',
    contextualSubtitle: 'Podcast licenses are valid for the world.',
  },
];

export interface IMediaTypeDefinitionsMap {
  [id: number]: IMediaTypeDefinitions;
}

export const mediaTypeDefinitionsMap: IMediaTypeDefinitionsMap = mediaTypeDefinitions.reduce((result, cur) => {
  result[cur.id] = cur;
  return result;
}, {});

export const componentHints: IComponentHint[] = [
  {
    // Baltic State
    id: 4004,
    hint: 'Includes Estonia, Latvia and Lithuania',
  },
  {
    // Eastern Europe
    id: 4005,
    hint: 'Includes Estonia, Latvia and Lithuania, Czech Republic, Hungary, Poland, Slovakia, Slovenia, Republic of Macedonia, Serbia and Montenegro, Bosnia and Herzegovina, Bulgaria, Albania, Croatia, Russia, Belarus, Ukraine, Romania, Moldova',
  },
  {
    // China
    id: 4007,
    hint: 'Includes Hong Kong, Macau, Taiwan and Singapore',
  },
  {
    // Gulf Cooperation Council
    id: 4008,
    hint: 'Bahrain, Kuwait, Oman, Qatar, Saudi Arabia, United Arab Emirates',
  },
  {
    // MENA
    id: 4010,
    hint: 'Includes Algeria, Bahrain, Egypt, Iran, Iraq, Israel, Jordan, Kuwait, Lebanon, Libya, Morocco, Oman, Palestine, Qatar Sahrawi Arab Democratic Republic, Saudi Arabia, Syria, Tunisia, United Arab Emirates, Yemen, Afganistan, Armenia, Azerbaijan, Chad, Comoros, Cyprus, Djibouti, Eritrea, Georgia, Mali, Mauritania, Niger, Pakistan, Somalia and Sudan (doesn\'t include Turkey)',
  },
  {
    // MENAT
    id: 4011,
    hint: 'Includes all countries listed under MENA with the addition of Turkey',
  },
  {
    // Portugal, Brazil and CPLP
    id: 4013,
    hint: 'Includes Portugal, Brazil, Angola, Cape Verde, Guinea-Bissau, Mozambique, São Tomé and Príncipe, Timor-Leste, and Equatorial Guinea.',
  },
  {
    // Southeast Asia
    id: 4014,
    hint: 'Includes Brunei, Cambodia, East Timor, Indonesia, Laos, Malaysia, Myanmar, Philippines, Singapore, Thailand, Vietnam, Christmas Island, Coco (Keeling) Islands and Andaman and Nicobar Islands',
  },
  {
    // Western Europe
    id: 4015,
    hint: 'Includes Belgium, France, Ireland, Luxembourg, Monaco, Netherlands, UK, Andorra, Portugal and Spain',
  },
  {
    // UK
    id: 4018,
    hint: 'Includes Northern Ireland, Scotland, Wales and all British overseas territories',
  },
  {
    // France
    id: 4019,
    hint: 'Includes all French overseas regions, departments, territories and collectivities',
  },
  {
    // GAS - DACH
    id: 4020,
    hint: 'Includes Germany, Austria and Switzerland',
  },
  {
    // Netherlands
    id: 4031,
    hint: 'Includes SSS and ABC Islands',
  },
  {
    // USA - Regional
    id: 4039,
    hint: (
      <>
        Defined as as the broadcast of content in less than four US Television Market Areas (TMA). A Full list of TMAs can be found here:
        <Button
          size="medium"
          label="https://en.wikipedia.org/wiki/List_of_United_States_television_markets"
          variant="hyperlink"
          wrapperComponent="a"
          hyperLinkProps={{
            href: 'https://en.wikipedia.org/wiki/List_of_United_States_television_markets',
            target: '_blank',
          }}
          style={{}}
        />
      </>
    ),
  },
];



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/forms/LicenseBuilder/config.tsx